const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Parse the YYYY-MM-DD portion of a stored ISO date (avoids the UTC → previous-day shift in
// timezones west of UTC — stored schedule dates are UTC midnight).
function ymd(iso: string): { y: number; m: number; d: number } {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return { y, m: m - 1, d };
}

/**
 * Human date range for a date-mode initiative, e.g. "Nov 27 – Dec 7".
 * Adds the year only when the range crosses calendar years ("Dec 20 '26 – Jan 8 '27").
 * With no end date returns "From Nov 1". Returns null when there is no start date.
 */
export function formatDateRange(startIso?: string | null, endIso?: string | null): string | null {
  if (!startIso) return null;
  const s = ymd(startIso);
  if (!endIso) return `From ${MONTHS[s.m]} ${s.d}`;
  const e = ymd(endIso);
  const cross = s.y !== e.y;
  // Same month & year → collapse to "Aug 3 – 7" so the pill stays compact.
  if (!cross && s.m === e.m) return `${MONTHS[s.m]} ${s.d} – ${e.d}`;
  const start = `${MONTHS[s.m]} ${s.d}${cross ? ` '${String(s.y).slice(2)}` : ""}`;
  const end = `${MONTHS[e.m]} ${e.d}${cross ? ` '${String(e.y).slice(2)}` : ""}`;
  return `${start} – ${end}`;
}

/**
 * Single stored ISO date rendered in UTC, e.g. "Nov 1, 2026" (matches the detail modal).
 * Returns "-" when there is no date.
 */
export function formatActualDate(iso?: string | null): string {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * Small hint under a date-mode Start/End date showing which of the tenant's own configured
 * timeframe periods it falls in, e.g. "2026 Q3 · Aug'26" or "FY26 Q3 · Aug'26" (whatever prefix
 * the tenant's Timeframes settings use). The quarter number always uses standard calendar
 * quarters (Q1=Jan-Mar, Q2=Apr-Jun, Q3=Jul-Sep, Q4=Oct-Dec) — independent of the matched
 * period's own label — since that's the one thing that must stay consistent across tenants.
 * Falls back to the plain calendar year as the prefix when no configured period covers the date
 * (e.g. the date sits outside the tenant's seeded timeframe range). Returns null with no date.
 */
export function formatFiscalQuarterHint(
  iso: string | null | undefined,
  quarters: { fullLabel: string; startDate: string; endDate: string }[]
): string | null {
  if (!iso) return null;
  const { y, m, d } = ymd(iso);
  const dateMs = Date.UTC(y, m, d);
  const quarterNum = Math.floor(m / 3) + 1;
  const yy = String(y).slice(2);

  const match = quarters.find((q) => {
    const s = new Date(q.startDate).getTime();
    const e = new Date(q.endDate).getTime();
    return dateMs >= s && dateMs <= e;
  });
  // Strip the trailing month token off the matched period's label to get its prefix
  // ("2026 Aug" → "2026", "FY26 Aug" → "FY26"); fall back to the plain calendar year.
  const prefix = match ? match.fullLabel.replace(/\s+\S+$/, "") : String(y);

  return `${prefix} Q${quarterNum} · ${MONTHS[m]}'${yy}`;
}
